import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { templateHeaderSectionContentfulRelayFragment$key } from "@/__generated_contentful__/templateHeaderSectionContentfulRelayFragment.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";

import { TemplateCtaButtons } from "../../../template-details-page/template-cta-buttons";
import { TemplateAuthor } from "../../components/contentful-relay-components/template-author-contentful-relay";

const headerSectionStyles = css({
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "1px",
    borderBlockEndColor: token("color.background.accent.gray.subtler.hovered"),
    display: "flex",
    flexDirection: "column",
    paddingBlockEnd: "24px",
    [media.below.sm]: {
        alignItems: "center"
    }
});

const titleStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#172B4D",
    fontFamily: "Charlie Display",
    fontSize: "52px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "60px",
    marginBlockEnd: "16px",
    marginBlockStart: 0,
    overflowWrap: "break-word",
    [media.below.sm]: {
        textAlign: "center"
    }
});

const subTitleStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#172B4D",
    fontFamily: "Charlie Text",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "28px",
    marginBlockEnd: "32px",
    marginBlockStart: 0,
    [media.below.sm]: {
        textAlign: "center"
    }
});

const TemplateHeaderSectionContentfulRelayFragment = graphql`
    fragment templateHeaderSectionContentfulRelayFragment on ProductTemplate {
        templateNameExternal
        oneLinerHeadline
        pluginModuleKey
        ...templateAuthorContentfulRelayFragment
    }
`;

export type TemplateHeaderSectionProps = {
    template: templateHeaderSectionContentfulRelayFragment$key;
};

export const TemplateHeaderSection = ({
    template: templateRef
}: TemplateHeaderSectionProps) => {
    const template = useFragment(
        TemplateHeaderSectionContentfulRelayFragment,
        templateRef
    );
    const { templateNameExternal, oneLinerHeadline, pluginModuleKey } =
        template;

    const intl = useIntl();

    const templateName = intl.formatMessage(
        ConfluenceTemplateMessages.templateTitle,
        {
            templateName: templateNameExternal ?? ""
        }
    );

    const renderCtaButons = () => {
        return (
            <TemplateCtaButtons
                name={templateName}
                pluginModuleKey={pluginModuleKey || ""}
            />
        );
    };

    return (
        <section
            css={[headerSectionStyles]}
            data-testid="TemplateHeaderSection"
        >
            <h1 css={[titleStyles]}>{templateName}</h1>
            <TemplateAuthor template={template} />
            <p css={[subTitleStyles]}>{oneLinerHeadline}</p>
            {renderCtaButons()}
        </section>
    );
};
